import { T, useGT } from 'gt-react';
import { useListWorkflowDefinitionSummaries } from 'src/api/rpc/hooks/workflows/useWorkflowDefinitions';
import {
  SelectDropdown,
  type SelectDropdownOption,
} from 'src/components/common/SelectDropdown';

/**
 * Published workflows (lineages) in the org — a `workflow` rule owner is
 * triggered with the invoice when its exception type fires.
 */
export function WorkflowPicker({
  value,
  disabled,
  onChange,
}: {
  value: string;
  disabled?: boolean;
  onChange: (workflowId: string) => void;
}) {
  const gt = useGT();
  const { data, isLoading, error } = useListWorkflowDefinitionSummaries({
    limit: 200,
    published: true,
    sortBy: 'name',
    sortDirection: 'asc',
  });
  const options: SelectDropdownOption[] = (data?.workflows ?? []).map(
    (workflow) => ({
      id: workflow.id,
      label: workflow.description
        ? `${workflow.name} — ${workflow.description}`
        : workflow.name,
      display: workflow.name,
    }),
  );
  const selected = options.find((option) => option.id === value) ?? null;

  return (
    <div className="flex flex-col gap-1">
      <SelectDropdown
        options={options}
        value={selected}
        onChange={(option) => onChange(option?.id ?? '')}
        placeholder={gt('Select a workflow…', {
          context: 'placeholder for the exception rule workflow owner picker',
        })}
        searchPlaceholder={gt('Search workflows…', {
          context: 'search box in the exception rule workflow owner picker',
        })}
        size="sm"
        fullWidth
        disabled={disabled || isLoading}
      />
      {error ? (
        <p className="text-xs text-destructive">
          <T context="error loading workflows for the exception rule owner picker">
            Could not load workflows.
          </T>
        </p>
      ) : null}
    </div>
  );
}
